/* eslint-disable react/require-default-props */
import React, { useState, useEffect, useRef } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { Accordion } from '../Accordion';
import { StarSmall, StarSmallOutline, CloseIcon } from '../Icons/icons';
import { Title, Text } from '../Typography';

import { FilterOption, FilterTopic, IFilterMenuProps } from './FilterMenu';
import { FilterTagsList } from './FilterTagsList';

import {
  MenuWrapper,
  FilterHeader,
  Break,
  FiltersTitle,
  ClearButtonLayoutHelper,
  ClearFilterButton,
  CloseButton,
  FilterTopicWrapper,
  FilterCheckOption,
  RatingsStars,
  FilterContent,
  MobileApplyButtonWrapper,
  DrawerOverlay,
} from './styles';

const ClearFiltersControl = ({
  filtersCount,
  label = 'Clear Filters',
  onClear,
}: {
  filtersCount: number;
  label?: string;
  onClear: () => void;
}) =>
  filtersCount < 1 ? (
    <></>
  ) : (
    <ClearFilterButton type="button" onClick={onClear}>
      <CloseIcon />{' '}
      <Text tag="span" type="leadxs">
        {label} ({filtersCount})
      </Text>
    </ClearFilterButton>
  );

const getTopicTitle = (t: string) => (
  <Title tag="h2" type="xxs">
    {t}
  </Title>
);

const getPeekText = (t: FilterTopic) => {
  const contents = t.options.reduce((res: string, curr: FilterOption) => {
    if (curr.selected) {
      return res.length > 0 ? [res, curr.label].join(', ') : curr.label;
    }
    return res;
  }, '');
  return contents.length ? (
    <Text tag="div" type="sm" className="text-gray-dark pt-0.5 whitespace-nowrap truncate">
      {contents}
    </Text>
  ) : null;
};

const getDisplayStars = (r: number) => (
  <RatingsStars>
    {[1, 2, 3, 4, 5].map((n) =>
      n > r ? (
        <StarSmallOutline key={`rating-star-${n}`} />
      ) : (
        <StarSmall key={`rating-star-${n}`} />
      ),
    )}
  </RatingsStars>
);

const getTopicOptions = (t: FilterTopic, onToggleFilter: Function) => (
  <FilterTopicWrapper key={t.key}>
    {t.options.map((opt, idx) => (
      <FilterCheckOption htmlFor={opt.key} key={opt.key}>
        <input
          type="checkbox"
          id={opt.key}
          name={opt.key}
          onChange={() => onToggleFilter(opt.key, !opt.selected)}
          value={opt.key}
          data-test={`filter-${t.key}-opt-${opt.key}`}
          checked={opt.selected}
        />
        <Text tag="span" type="md" className={opt.selected ? 'font-medium' : ''}>
          {t.type === 'rating' && getDisplayStars(idx)}
          {opt.label} ({opt.itemCount})
        </Text>
      </FilterCheckOption>
    ))}
  </FilterTopicWrapper>
);

export const FilterMenu = ({
  id = 'filter-menu',
  isOpen,
  title,
  filters,
  onClose,
  onApplyFilters,
  totalResults,
  labels,
}: IFilterMenuProps) => {
  const [currentFilters, setCurrentFilters] = useState<FilterTopic[]>(filters);
  const [filtersDirty, setFiltersDirty] = useState<boolean>(false);
  const scrollLockRef = useRef(null);

  useEffect(() => {
    setCurrentFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (scrollLockRef.current) {
      if (isOpen) {
        disableBodyScroll(scrollLockRef.current);
      } else {
        enableBodyScroll(scrollLockRef.current);
      }
    }
  }, [isOpen]);

  const onToggleFilter = (filterKey: string, selected: boolean = false) => {
    const updatedFilters: FilterTopic[] = currentFilters.reduce(
      (updated, current: FilterTopic) => [
        ...updated,
        {
          ...current,
          options: current.options.map((opt) =>
            opt.key === filterKey ? { ...opt, selected } : opt,
          ),
        },
      ],
      [] as FilterTopic[],
    );
    setCurrentFilters(updatedFilters);
    setFiltersDirty(true);
    onApplyFilters(updatedFilters);
  };

  const clearAllFilters = () => {
    const updatedFilters: FilterTopic[] = currentFilters.map((cf: FilterTopic) => ({
      ...cf,
      options: cf.options.map((opt: FilterOption) => ({
        ...opt,
        selected: false,
      })),
    }));
    setCurrentFilters(updatedFilters);
    setFiltersDirty(true);
    onApplyFilters(updatedFilters);
  };

  const tagList: FilterOption[] = currentFilters.reduce((a: FilterOption[], t: FilterTopic) => {
    const selected: FilterOption[] = t.options.reduce(
      (opts: FilterOption[], curr: FilterOption) => (curr.selected ? [...opts, curr] : opts),
      [] as FilterOption[],
    );
    return [...a, ...selected];
  }, [] as FilterOption[]);

  return (
    <>
      {isOpen && <DrawerOverlay />}
      <MenuWrapper id={id} data-test={id} $isOpen={isOpen}>
        <FilterHeader>
          <FiltersTitle tag="h2" type="xs">
            {title}
          </FiltersTitle>
          <ClearButtonLayoutHelper>
            <ClearFiltersControl
              filtersCount={tagList.length}
              label={labels.clearAllFilters}
              onClear={() => clearAllFilters()}
            />
          </ClearButtonLayoutHelper>
          <CloseButton type="button" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
          <Break />
        </FilterHeader>
        <FilterContent ref={scrollLockRef}>
          <FilterTagsList
            tagList={tagList}
            labels={labels}
            clearOnTagClick
            onClearFilter={(key: string) => onToggleFilter(key)}
          />
          <Accordion
            key={isOpen ? 'accordions-open' : 'accordions-closed'}
            data-test={`${id}-topics-accordion`}
            name="filter-topics"
            data={currentFilters.map((f) => ({
              title: getTopicTitle(f.label),
              peek: getPeekText(f),
              body: getTopicOptions(f, onToggleFilter),
            }))}
            collapsed
          />
          <MobileApplyButtonWrapper>
            <button
              type="button"
              onClick={() => {
                onApplyFilters(currentFilters);
                setFiltersDirty(false);
                onClose();
              }}
            >
              <span>
                {filtersDirty
                  ? `${labels.applyFilters} (${totalResults})`
                  : `${labels.showAllResults} (${totalResults})`}
              </span>
            </button>
          </MobileApplyButtonWrapper>
        </FilterContent>
      </MenuWrapper>
    </>
  );
};
